import { useState, useEffect, useRef } from 'react'
import { useAuth } from './useAuth'
import { supabase } from '../services/supabaseClient'
import { 
  fetchFlowsFromSalesforce, 
  updateSalesforceOrgSettings,
  disconnectOrganization
} from '../services/salesforceService'
import { callEdgeFunction, isEdgeFunctionMigrated, getEdgeFunctionName } from '../lib/edgeFunctionClient'


interface SalesforceOrg {
  // Based on DATABASE.md schema
  id: string
  account_id: string
  name: string
  instance_url: string
  refresh_token?: string
  is_sandbox: boolean
  created_at: string
  last_synced_at?: string | null
  production_org_id?: string
  org_id?: string
  connection_type?: string
  needs_reconnect?: boolean

  // Additional properties for UI (not in database schema)
  isShared?: boolean
  sharedBy?: string
  accessLevel?: string
  syncStatus?: 'idle' | 'syncing' | 'error' // Add sync status tracking
}

interface UseSalesforceOptions {
  skipInitialFetch?: boolean;
}

export function useSalesforce(options: UseSalesforceOptions = {}) {
  const { skipInitialFetch = false } = options;
  const { user } = useAuth()
  const [orgs, setOrgs] = useState<SalesforceOrg[]>([])
  const [isLoading, setIsLoading] = useState(!skipInitialFetch)
  const [error, setError] = useState<Error | null>(null)
  const [userRole, setUserRole] = useState<string | null>(null)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [currentAccountId, setCurrentAccountId] = useState<string | null>(null)
  
  // No longer needed - simplified approach
  // We'll directly trigger a sync when an org is connected rather than tracking it
  
  // Track current account ID changes
  useEffect(() => {
    const checkAccountId = () => {
      const accountId = localStorage.getItem('currentAccountId');
      setCurrentAccountId(accountId);
    };

    // Initial check
    checkAccountId();

    // Listen for storage events (changes from other tabs/windows)
    window.addEventListener('storage', checkAccountId);

    // Also listen for custom event (for same tab changes)
    window.addEventListener('accountChanged', checkAccountId);

    return () => {
      window.removeEventListener('storage', checkAccountId);
      window.removeEventListener('accountChanged', checkAccountId);
    };
  }, []);

  // Use effect to check if user is a Super Admin
  useEffect(() => {
    if (!user || !currentAccountId) {
      setUserRole(null);
      setIsSuperAdmin(false);
      return;
    }

    const checkUserRole = async () => {
      try {


        // Get the role for this user in the CURRENT account only
        const { data: userAccountRole, error: roleError } = await supabase
          .from('user_account_roles')
          .select(`
            *,
            roles (
              name
            )
          `)
          .eq('user_id', user.id)
          .eq('account_id', currentAccountId)
          .single();

        if (roleError) {
          console.error('Error fetching user role:', roleError);
          setUserRole('Guest');
          setIsSuperAdmin(false);
          return;
        }

        if (!userAccountRole) {
          console.log('No role found for user in current account');
          setUserRole('Guest');
          setIsSuperAdmin(false);
          return;
        }

        const roleName = userAccountRole.roles?.name || 'Guest';
        setUserRole(roleName);
        setIsSuperAdmin(roleName === 'Super-Admin');
        console.log(`User role in account ${currentAccountId}: ${roleName}`);
      } catch (err) {
        console.error('Error checking user role:', err);
        setUserRole('Guest');
        setIsSuperAdmin(false);
      }
    };

    checkUserRole();
  }, [user, currentAccountId]);
  
  useEffect(() => {
    // Return early if no user is authenticated, skipInitialFetch is true, or no currentAccountId
    if (!user || skipInitialFetch || !currentAccountId) {
      if (!skipInitialFetch && user && !currentAccountId) {
        // User exists but no account selected yet - keep loading
        setIsLoading(true)
      } else {
        setOrgs([])
        setIsLoading(false)
      }
      return
    }
    
    // Track if the component is mounted to prevent state updates after unmount
    let isMounted = true;
    
    // Use a debounce mechanism to prevent multiple rapid fetches
    const fetchDebounceTimeout = setTimeout(async () => {
      if (!isMounted) return;
      
      setIsLoading(true)
      
      try {
        // First check if the table exists to handle missing schema gracefully
        try {
          const { error: tableCheckError } = await supabase
            .from('sf_orgs')
            .select('id', { count: 'exact', head: true })
            .limit(1)
          
          if (tableCheckError) {
            console.error('Table check error:', tableCheckError);
            if (tableCheckError.message.includes('relation "sf_orgs" does not exist')) {
              console.log('Salesforce orgs table does not exist');
              if (isMounted) setOrgs([])
              return
            }
            throw tableCheckError;
          }
        } catch (tableError) {
          console.error('Exception during table check:', tableError);
          setError(tableError instanceof Error ? tableError : new Error(String(tableError)));
          if (isMounted) setIsLoading(false);
          return;
        }
        
        // Verify the user has access to the current account
        const { data: accountAccess } = await supabase
          .from('user_account_roles')
          .select('account_id')
          .eq('user_id', user.id)
          .eq('account_id', currentAccountId)
          .single();

        if (!accountAccess) {
          console.log('User does not have access to current account');
          if (isMounted) {
            setOrgs([]);
            setIsLoading(false);
          }
          return;
        }

        // Check user's role in the CURRENT account to determine org visibility
        const { data: userAccountRole } = await supabase
          .from('user_account_roles')
          .select(`
            roles (
              name
            )
          `)
          .eq('user_id', user.id)
          .eq('account_id', currentAccountId)
          .single();

        const roleName = userAccountRole?.roles?.name || 'Viewer';
        const isAdminRole = ['Admin', 'Super-Admin'].includes(roleName);

        let ownedOrgs = [];

        if (isAdminRole) {
          // Admins and Super-Admins see all orgs in their CURRENT account only
          const ownedResult = await supabase
            .from('sf_orgs')
            .select('*')
            .eq('account_id', currentAccountId)
            .order('created_at', { ascending: false });

          if (ownedResult.error) throw ownedResult.error;
          ownedOrgs = ownedResult.data || [];
        } else {
          // Non-admins only see orgs they have explicit access to
          // We'll fetch these through user_org_access below
          console.log('Non-admin user - will only show orgs with explicit access');
        }
        
        // Initialize shared orgs array (default to empty)
        let sharedOrgs = [];

        // For non-admins, user_org_access is the ONLY source of orgs
        // For admins, it's additional orgs from other accounts they might have access to
        if (!isAdminRole) {
          // Non-admins ONLY see orgs they have explicit access to IN THE CURRENT ACCOUNT
          try {
            // First, get the orgs that belong to the current account
            const { data: accountOrgs, error: accountOrgsError } = await supabase
              .from('sf_orgs')
              .select('id')
              .eq('account_id', currentAccountId);

            if (accountOrgsError) {
              console.error('Error fetching account orgs:', accountOrgsError);
              ownedOrgs = [];
            } else if (accountOrgs && accountOrgs.length > 0) {
              const accountOrgIds = accountOrgs.map(org => org.id);

              // Now check which of these orgs the user has access to
              const { data: userOrgAccess, error: userOrgAccessError } = await supabase
                .from('user_org_access')
                .select('org_id')
                .eq('user_id', user.id)
                .in('org_id', accountOrgIds); // Only check orgs in current account

              if (!userOrgAccessError && userOrgAccess && userOrgAccess.length > 0) {
                const accessibleOrgIds = userOrgAccess.map(access => access.org_id);
                console.log(`Non-admin user has access to ${accessibleOrgIds.length} orgs in account ${currentAccountId}`);

                // Fetch the full org details for accessible orgs
                const { data: accessOrgs, error: accessOrgsError } = await supabase
                  .from('sf_orgs')
                  .select('*')
                  .in('id', accessibleOrgIds)
                  .order('created_at', { ascending: false });

                if (!accessOrgsError && accessOrgs) {
                  ownedOrgs = accessOrgs;
                  console.log(`Non-admin can see ${ownedOrgs.length} orgs`);
                }
              } else {
                console.log('Non-admin user has no org access in this account');
                ownedOrgs = [];
              }
            } else {
              console.log('No orgs exist in current account');
              ownedOrgs = [];
            }
          } catch (userOrgError) {
            console.error('Error checking user_org_access for non-admin:', userOrgError);
            ownedOrgs = [];
          }
        }

        // Check for guest access to orgs
        try {
          // First check if the guest_org_access table exists
          const { error: guestAccessError } = await supabase
            .from('guest_org_access')
            .select('id', { count: 'exact', head: true })
            .limit(1);
            
          if (guestAccessError) {
            console.log('Skipping guest org access check - table may not exist or not be accessible');
          } else {
            // Table exists, try to fetch guest access records
            try {
              const { data: guestAccess, error: guestFetchError } = await supabase
                .from('guest_org_access')
                .select('org_id')
                .eq('user_id', user.id);
                
              if (guestFetchError) {
                console.error('Error fetching guest org access:', guestFetchError);
              } else if (guestAccess && guestAccess.length > 0) {
                console.log(`Found ${guestAccess.length} orgs with guest access`);
                // In the future, fetch these orgs separately
              }
            } catch (guestError) {
              console.error('Exception during guest org access fetch:', guestError);
            }
          }
        } catch (tableCheckError) {
          console.error('Error checking guest_org_access table:', tableCheckError);
        }
        
        // Combine owned and shared orgs (avoiding duplicates)
        const allOrgs = [...ownedOrgs, ...sharedOrgs];
        
        if (isMounted) {
          setOrgs(allOrgs)
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error(String(err)))
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }, 300); // 300ms debounce to prevent rapid re-fetches
    
    return () => {
      isMounted = false;
      clearTimeout(fetchDebounceTimeout);
    }
  }, [user, currentAccountId])

  // Function to disconnect a Salesforce org
  const disconnectOrg = async (orgId: string) => {
    try {
      console.log(`Disconnecting Salesforce org ${orgId}...`);
      
      // Call the service function directly
      const result = await disconnectOrganization(orgId);
      console.log('Disconnect org success:', result);
      
      // Update local state - remove the disconnected org
      setOrgs(orgs.filter(org => org.id !== orgId));
      
      return { success: true };
    } catch (err) {
      console.error('Error disconnecting Salesforce org:', err);
      return { success: false, error: err };
    }
  }
  
  // Rate limiting for sync operations
  const lastSyncTimes = new Map<string, number>();
  const MIN_SYNC_INTERVAL_MS = 5000; // 5 seconds between syncs of the same org
  
  // Helper to check if an org was recently connected (in the last 30 seconds)
  const isRecentlyConnected = (org) => {
    if (!org || !org.created_at) return false;
    
    const createdAt = new Date(org.created_at);
    const now = new Date();
    const timeDiffSeconds = (now.getTime() - createdAt.getTime()) / 1000;
    
    // If org was created within the last 30 seconds, consider it "just connected"
    const isRecent = timeDiffSeconds < 30;
    console.log(`Org creation time check: created ${Math.round(timeDiffSeconds)}s ago, isRecent: ${isRecent}`);
    
    return isRecent;
  };
  
  // Function to sync a Salesforce org
  const syncOrg = async (orgId: string, forceRefresh = false) => {
    try {
      // Add debugging and reset any previous errors
      const syncSource = forceRefresh ? 'FORCED REFRESH' : 'USER REQUEST';
      console.log(`===== SYNC: ${syncSource} =====`);
      console.log(`Starting sync for org ${orgId}...${forceRefresh ? ' (forced refresh)' : ''}`);
      
      // First check if the org exists and if the user has access to it
      const { data: orgCheck, error: checkError } = await supabase
        .from('sf_orgs')
        .select('*')
        .eq('id', orgId)
        .single();
        
      if (checkError) {
        console.error('Error checking org existence:', checkError);
        throw checkError;
      }
      
      if (!orgCheck) {
        throw new Error('Organization not found');
      }
      
      // *** ALWAYS CHECK FOR CACHED DATA FIRST ***
      // Only skip the cache check if forceRefresh is true
      if (!forceRefresh) {
        console.log(`Checking for cached flows in database for org ${orgId}...`);
        
        // Check for flows in the database - use larger page size and count query
        const { data: existingFlows, error: flowsError, count } = await supabase
          .from('sf_flow_definitions')
          .select('*', { count: 'exact' })
          .eq('org_id', orgId)
          .limit(500); // Increase limit to make sure we get flows
        
        console.log(`Database query result: found ${count || 0} total flows, returned ${existingFlows?.length || 0} flows`);
          
        if (!flowsError && existingFlows && existingFlows.length > 0) {
          console.log(`Using ${existingFlows.length} cached flows from database`);
          
          // Update last access time in local state only
          setOrgs(prevOrgs => prevOrgs.map(org => 
            org.id === orgId 
              ? { ...org, last_synced_at: new Date().toISOString() } 
              : org
          ));
          
          return { success: true, flows: existingFlows.map(flow => ({
            Id: flow.id,
            MasterLabel: flow.master_label,
            FullName: flow.developer_name,
            ProcessType: flow.process_type,
            Description: flow.description,
            Status: flow.status,
            LastModifiedDate: flow.last_modified_date,
            LastModifiedBy: { Name: flow.metadata?.last_modified_by },
            NamespacePrefix: flow.metadata?.NamespacePrefix,
            ManageableState: flow.manageable_state,
            ActiveVersion: { Id: flow.active_version_id },
            fromDatabase: true
          }))};
        }
        
        // Add diagnostic check - if this is very recent org (connected < 10 seconds ago)
        // there's a high chance flows are still being synced in the background
        // so check if there's at least one flow and auto-refresh if not
        const orgJustConnected = isRecentlyConnected(orgCheck);
        if (orgJustConnected) {
          console.log('Org was connected very recently, flows may still be syncing. Checking flow count...');
          
          // Count flows for this org to see if any exist but weren't returned
          const { count: flowCount, error: countError } = await supabase
            .from('sf_flow_definitions')
            .select('*', { count: 'exact', head: true })
            .eq('org_id', orgId);
            
          console.log(`Flow count check: ${flowCount || 0} flows exist in database for this org`);
          
          if (!countError && flowCount && flowCount > 0) {
            console.log(`Found ${flowCount} flows in the database, but they weren't returned. Retrying database query...`);
            
            // If there are flows, retry the select with different parameters
            const { data: retryFlows, error: retryError } = await supabase
              .from('sf_flow_definitions')
              .select('*')
              .eq('org_id', orgId)
              .limit(200);
              
            if (!retryError && retryFlows && retryFlows.length > 0) {
              console.log(`Retry successful, using ${retryFlows.length} flows from database`);
              
              return { success: true, flows: retryFlows.map(flow => ({
                Id: flow.id,
                MasterLabel: flow.master_label,
                FullName: flow.developer_name,
                ProcessType: flow.process_type,
                Description: flow.description,
                Status: flow.status,
                LastModifiedDate: flow.last_modified_date,
                LastModifiedBy: { Name: flow.metadata?.last_modified_by },
                NamespacePrefix: flow.metadata?.NamespacePrefix,
                ManageableState: flow.manageable_state,
                ActiveVersion: { Id: flow.active_version_id },
                fromDatabase: true
              }))};
            }
            
            // If retry fails but flows exist, automatically force refresh
            console.log('Retry failed but flows exist. Forcing refresh now...');
            forceRefresh = true;
          }
        }
        
        // IMPORTANT: Don't automatically fall back to Salesforce sync
        // UNLESS forceRefresh is explicitly set to true (which means it's a new org connection)
        if (!forceRefresh) {
          console.log('No cached flows found, but not forcing Salesforce sync per user preference');
          return { 
            success: false, 
            error: new Error('No cached data found. Please click "Refresh from Salesforce" to sync data.'),
            flowsNeedSync: true,
            flows: []
          };
        }
        
        console.log('forceRefresh is true - this is likely a new org connection, proceeding with Salesforce sync');
      }
      
      // Only proceed with Salesforce sync if explicitly requested
      
      // Check if we've synced this org recently to prevent rapid API calls
      const lastSyncTime = lastSyncTimes.get(orgId) || 0;
      const now = Date.now();
      const timeSinceLastSync = now - lastSyncTime;
      
      if (!forceRefresh && timeSinceLastSync < MIN_SYNC_INTERVAL_MS) {
        console.log(`Org ${orgId} was synced ${timeSinceLastSync}ms ago, too soon to sync again`);
        return { 
          success: false, 
          error: new Error('Sync requested too soon after previous sync'),
          flows: []
        };
      }
      
      // Update the last sync time
      lastSyncTimes.set(orgId, now);

      // Update local state to show syncing - add syncStatus 'syncing'
      setOrgs(prevOrgs => prevOrgs.map(org => 
        org.id === orgId 
          ? { 
              ...org, 
              last_synced_at: new Date().toISOString(),
              syncStatus: 'syncing' // Set status to 'syncing' while in progress
            } 
          : org
      ));
      
      try {
        // Update the last_synced_at field per DATABASE.md schema
        try {
          console.log(`Updating last_synced_at for org ${orgId}`);
          const { error: statusError } = await supabase
            .from('sf_orgs')
            .update({
              last_synced_at: new Date().toISOString()
            })
            .eq('id', orgId);
            
          if (statusError) {
            console.error('Error updating last_synced_at:', statusError);
          } else {
            console.log(`Successfully updated ${orgId} last_synced_at timestamp`);
          }
        } catch (statErr) {
          // Don't let this prevent flow sync
          console.error('Error updating last_synced_at:', statErr);
        }
      } catch (dbError) {
        // Log but don't block the sync process
        console.error('Database error updating sync status:', dbError);
      }
      
      try {
        // Fetch flows from Salesforce
        console.log(`Starting flow fetch for org ${orgId}`);
        const apiResponse = await fetchFlowsFromSalesforce(orgId);
        
        // CRITICAL FIX: Try simplified_flows first, then original flows, then saved_flows as fallback
        // This ensures flows are displayed in the simplest format that works
        const flows = apiResponse.simplified_flows || apiResponse.flows || [];
        console.log(`Flow fetch complete for org ${orgId}. Got ${flows?.length || 0} flows from ${apiResponse.simplified_flows ? 'simplified API response' : 'original API response'}`);
        
        // Log database status without affecting flow display  
        if (apiResponse.database) {
          console.log(`Database save status: ${apiResponse.database.saved || 0} saved, ${apiResponse.database.failed || 0} failed`);
        }
        
        // Simplified summary to help debug
        console.log('Flow data overview:', {
          totalFlows: flows.length,
          hasSimplifiedFlows: !!apiResponse.simplified_flows,
          savedFlowsCount: apiResponse.saved_flows?.length || 0,
          flowStructure: flows.length > 0 ? Object.keys(flows[0]) : []
        });
        
        // Update last_synced_at and set syncStatus to 'idle' on success
        setOrgs(prevOrgs => prevOrgs.map(org => 
          org.id === orgId 
            ? { 
                ...org, 
                last_synced_at: new Date().toISOString(),
                syncStatus: 'idle' // Reset to idle when sync is complete
              } 
            : org
        ));
        
        // Update the last_synced_at timestamp per DATABASE.md schema
        try {
          const { error: updateError } = await supabase
            .from('sf_orgs')
            .update({
              last_synced_at: new Date().toISOString()
            })
            .eq('id', orgId);
          
          if (updateError) {
            console.error('Error updating org sync status:', updateError);
          } else {
            console.log(`Successfully updated sync status to success for org ${orgId}`);
          }
        } catch (updateErr) {
          console.error('Error updating sync status:', updateErr);
        }
        
        return { 
          success: true, 
          flows: flows, // Use the already selected, simplified flows
          original_flows: apiResponse.flows, // Keep original flows as backup
          database_stats: apiResponse.database
        };
      } catch (syncError) {
        console.error('Error during Salesforce sync:', syncError);
        
        // Look for cached data again as a fallback
        console.log('Trying to use cached data as fallback after sync error');
        const { data: fallbackFlows } = await supabase
          .from('sf_flow_definitions')
          .select('*')
          .eq('org_id', orgId);
          
        if (fallbackFlows && fallbackFlows.length > 0) {
          console.log(`Using ${fallbackFlows.length} cached flows from database as fallback`);
          
          // Still mark as failed but return cached data - use last_synced_at as per DATABASE.md
          setOrgs(orgs.map(org => 
            org.id === orgId 
              ? { ...org, last_synced_at: new Date().toISOString() } 
              : org
          ));
          
          // Record the failure in the database too - only update last_synced_at per DATABASE.md
          try {
            await supabase
              .from('sf_orgs')
              .update({
                last_synced_at: new Date().toISOString()
              })
              .eq('id', orgId);
          } catch (dbErr) {
            console.log('Could not update status (non-critical):', dbErr);
          }
          
          return { 
            success: true, 
            warning: 'Using cached data due to Salesforce sync failure',
            flows: fallbackFlows.map(flow => ({
              Id: flow.id,
              MasterLabel: flow.master_label,
              DeveloperName: flow.developer_name,
              ProcessType: flow.process_type,
              Description: flow.description,
              Status: flow.status,
              LastModifiedDate: flow.last_modified_date,
              LastModifiedBy: { Name: flow.metadata?.last_modified_by },
              NamespacePrefix: flow.metadata?.NamespacePrefix,
              ManageableState: flow.manageable_state,
              ActiveVersion: { Id: flow.active_version_id },
              fromDatabase: true
            }))
          };
        }
        
        // No fallback data available, rethrow the error
        throw syncError;
      }
    } catch (err) {
      console.error('Error syncing Salesforce org:', err);
      
      // Check if this is an expired token error (special handling)
      const isExpiredTokenError = 
        err instanceof Error && 
        (err.name === 'TokenExpiredError' || 
         err.message.includes('expired') && err.message.includes('token'));
         
      if (isExpiredTokenError) {
        console.warn('Expired token detected during sync, marking org as needing reconnection');
        
        // Update local state to show connection expired using our interface fields
        // We'll use a custom field "needsReconnect" for UI purposes only
        setOrgs(orgs.map(org => 
          org.id === orgId 
            ? { 
                ...org, 
                last_synced_at: new Date().toISOString(),
                needsReconnect: true
              } 
            : org
        ));
        
        // Update database to indicate reconnection needed
        try {
          // According to DATABASE.md, we can only update last_synced_at here
          // We'd need to modify the schema to add error_message or connection_status
          await supabase
            .from('sf_orgs')
            .update({
              last_synced_at: new Date().toISOString()
            })
            .eq('id', orgId);
        } catch (updateErr) {
          console.error('Error updating sync status for expired token:', updateErr);
        }
        
        return { 
          success: false, 
          error: err,
          connectionExpired: true,
          flows: []
        };
      }
      
      // Regular error handling
      // Update local state with last_synced_at and set error status
      setOrgs(orgs.map(org => 
        org.id === orgId 
          ? { 
              ...org, 
              last_synced_at: new Date().toISOString(),
              syncStatus: 'error' // Set to error when sync fails
            } 
          : org
      ));
      
      // Try to update sync status to failed in database
      // Update last_synced_at based on DATABASE.md schema
      try {
        await supabase
          .from('sf_orgs')
          .update({
            last_synced_at: new Date().toISOString()
          })
          .eq('id', orgId);
      } catch (updateErr) {
        console.error('Error updating last_synced_at:', updateErr);
      }
      
      return { 
        success: false, 
        error: err,
        flows: []
      };
    }
  }
  
  // Save a Salesforce org
  const saveSalesforceOrg = async (userId: string, tokenData: any, userInfo: any) => {
    try {
      // Get the environment from localStorage
      const environment = localStorage.getItem('sf_env') || 'production';
      
      // First get the user's accounts
      const { data: accountsData, error: accountsError } = await supabase
        .from('user_account_roles')
        .select('account_id')
        .eq('user_id', userId);
        
      if (accountsError) {
        console.error('Error fetching user accounts for saving org:', accountsError);
        throw accountsError;
      }
      
      if (!accountsData || accountsData.length === 0) {
        throw new Error('No accounts found for user. Please ensure your account is set up properly.');
      }
      
      // Use the first account (most users will have just one)
      const accountId = accountsData[0].account_id;
      console.log(`Using account ID ${accountId} for new Salesforce org`);
      
      // Combine the data that we need - using fields from DATABASE.md schema
      const orgData = {
        account_id: accountId,
        name: userInfo.display_name || 'Salesforce Org',
        instance_url: tokenData.instance_url || 'https://example.my.salesforce.com',
        is_sandbox: (tokenData.instance_url || '').includes('.sandbox.'),
        access_token: tokenData.access_token,
        refresh_token: tokenData.refresh_token,
        token_type: tokenData.token_type || 'Bearer',
        scope: tokenData.scope || 'api refresh_token',
        environment: environment // Include the environment
      };
      
      console.log('Saving Salesforce org with data:', {
        ...orgData,
        access_token: 'REDACTED',
        refresh_token: 'REDACTED'
      });
      
      // Save to the database directly using Supabase
      const { data: result, error: saveError } = await supabase
        .from('sf_orgs')
        .upsert({
          user_id: user?.id,
          org_id: userInfo.organization_id,
          ...orgData
        }, { 
          onConflict: 'org_id',
          returning: 'representation'
        })
        .select()
        .single();
      
      if (saveError) {
        throw saveError;
      }
      
      // Update the local state
      setOrgs(prev => {
        const existingIndex = prev.findIndex(org => org.instance_url === orgData.instance_url);
        // Add syncStatus to the org record
        const orgWithStatus = {
          ...result,
          syncStatus: 'syncing' // Set initial status to syncing for new orgs
        };
        
        if (existingIndex >= 0) {
          // Replace the existing org
          const updated = [...prev];
          updated[existingIndex] = orgWithStatus;
          return updated;
        } else {
          // Add the new org
          return [orgWithStatus, ...prev];
        }
      });
      
      return result;
    } catch (err) {
      console.error('Error saving Salesforce org:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  };

  // Function to update org settings
  const updateOrgSettings = async (
    orgId: string, 
    settings: { filter_active_only?: boolean, filter_package_state?: string }
  ) => {
    try {
      const updatedOrg = await updateSalesforceOrgSettings(orgId, settings);
      
      // Update local state with the updated org
      setOrgs(prevOrgs => 
        prevOrgs.map(org => 
          org.id === orgId ? { ...org, settings: updatedOrg.settings } : org
        )
      );
      
      return { success: true, org: updatedOrg };
    } catch (err) {
      console.error('Error updating org settings:', err);
      return { success: false, error: err };
    }
  };

  // Function to get detailed organization info
  const getOrgInfo = async (accessToken: string, instanceUrl: string) => {
    try {
      console.log('Getting organization info from Salesforce');
      
      // Call the Salesforce API to get user info
      const response = await fetch(`${instanceUrl}/services/oauth2/userinfo`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch user info: ${response.statusText}`);
      }
      
      const orgInfo = await response.json();
      return { success: true, orgInfo };
    } catch (err) {
      console.error('Error getting organization info:', err);
      return { success: false, error: err };
    }
  };

  // Function to refresh flow summary
  const refreshFlowSummary = async (orgId: string, flowId: string, workspaceId?: string) => {
    try {
      console.log(`Refreshing flow analysis for flow ${flowId} in org ${orgId}${workspaceId ? ` for workspace ${workspaceId}` : ''}`);
      
      // Get the current session to get the access token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('No active session found');
      }
      
      const endpoint = '/api/salesforce/flows/regenerate-summary';
      let result;
      
      if (isEdgeFunctionMigrated(endpoint)) {
        // Use Edge Function
        const functionName = getEdgeFunctionName(endpoint);
        result = await callEdgeFunction(functionName!, { 
          orgId, 
          flowId,
          workspaceId,
          priority: 'critical'
        });
      } else {
        // Fallback to Vercel API
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ 
            orgId, 
            flowId,
            workspaceId,
            priority: 'critical'
          }),
        });
        
        if (!response.ok) {
          let errorMessage = `Failed to refresh summary: ${response.statusText}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (e) {
            // If response isn't JSON, use the default message
          }
          throw new Error(errorMessage);
        }
        
        result = await response.json();
      }
      
      console.log('Flow summary refresh initiated:', result);
      
      return { success: true, result };
    } catch (err) {
      console.error('Error refreshing flow summary:', err);
      throw err;
    }
  };

  return {
    orgs,
    isLoading,
    error,
    userRole,
    isSuperAdmin,
    disconnectOrg,
    syncOrg, // Takes optional forceRefresh parameter
    saveSalesforceOrg,
    updateOrgSettings,
    getOrgInfo,
    refreshFlowSummary,
    refetch: async () => {
      if (user) {
        setIsLoading(true)
        
        try {
          let accountIds: string[] = [];
          
          // First get the user's accounts - check both roles and owned accounts
          try {
            // Check accounts through roles
            const { data: roleAccountsData, error: roleAccountsError } = await supabase
              .from('user_account_roles')
              .select('account_id')
              .eq('user_id', user.id);
              
            if (roleAccountsError) {
              console.error('Error fetching user role accounts in refetch:', roleAccountsError);
            }
            
            // Also check for accounts where user is the owner
            const { data: ownedAccountsData, error: ownedAccountsError } = await supabase
              .from('accounts')
              .select('id')
              .eq('owner_id', user.id);
              
            if (ownedAccountsError) {
              console.error('Error fetching owned accounts in refetch:', ownedAccountsError);
            }
            
            // Combine account IDs from both sources
            const roleAccountIds = roleAccountsData?.map(row => row.account_id) || [];
            const ownedAccountIds = ownedAccountsData?.map(row => row.id) || [];
            
            // Use a Set to remove duplicates
            const allAccountIds = [...new Set([...roleAccountIds, ...ownedAccountIds])];
            
            if (allAccountIds.length === 0) {
              console.log('No accounts found for user in refetch (neither through roles nor ownership)');
              // Don't show an error - just return empty orgs list for users without accounts
              setOrgs([]);
              setIsLoading(false);
              return;
            }
            
            // Use the combined account IDs
            accountIds = allAccountIds;
          } catch (accountError) {
            console.error('Exception during account fetch in refetch:', accountError);
            setError(accountError instanceof Error ? accountError : new Error(String(accountError)));
            setOrgs([]);
            setIsLoading(false);
            return;
          }
          
          console.log(`Found ${accountIds.length} accounts for user in refetch`);
          
          // Fetch orgs that belong to the user's accounts
          // Only query if we have account IDs
          const ownedResult = accountIds.length > 0 
            ? await supabase
                .from('sf_orgs')
                .select('*')
                .in('account_id', accountIds)
                .order('created_at', { ascending: false })
            : { data: [], error: null };
          if (ownedResult.error) {
            setError(ownedResult.error);
            return;
          }
          const ownedOrgs = ownedResult.data || [];
          
          // Initialize shared orgs array (default to empty)
          const sharedOrgs = [];
          
          // Check for guest access to orgs
          try {
            // First check if the guest_org_access table exists
            const { error: guestAccessError } = await supabase
              .from('guest_org_access')
              .select('id', { count: 'exact', head: true })
              .limit(1);
              
            if (guestAccessError) {
              console.log('Skipping guest org access check - table may not exist or not be accessible');
            } else {
              // Table exists, try to fetch guest access records
              try {
                const { data: guestAccess, error: guestFetchError } = await supabase
                  .from('guest_org_access')
                  .select('org_id')
                  .eq('user_id', user.id);
                  
                if (guestFetchError) {
                  console.error('Error fetching guest org access:', guestFetchError);
                } else if (guestAccess && guestAccess.length > 0) {
                  console.log(`Found ${guestAccess.length} orgs with guest access`);
                  // In the future, fetch these orgs separately
                }
              } catch (guestError) {
                console.error('Exception during guest org access fetch:', guestError);
              }
            }
          } catch (tableCheckError) {
            console.error('Error checking guest_org_access table:', tableCheckError);
          }
          
          // Combine orgs - only using owned orgs for now since shared org fetch was failing
          const allOrgs = [...ownedOrgs];
          setOrgs(allOrgs);
        } catch (err) {
          console.error('Error in refetch:', err)
          setError(err instanceof Error ? err : new Error(String(err)))
        } finally {
          setIsLoading(false)
        }
      }
    }
  }
}